import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "./auth/AuthContext";
import { ToastProvider } from "./components/ui/Toast";
import { ConfirmProvider } from "./components/ui/ConfirmDialog";
import { ThemeProvider } from "./theme/ThemeContext";
import "./i18n";
// TODO el CSS se importa acá, en este orden, aunque cada pantalla importe el
// suyo. Las pantallas se cargan diferidas (App.tsx) y, sin esto, Vite partía
// el CSS por chunk y lo inyectaba en el orden en que el usuario abre las
// pantallas: la cascada cambiaba según por dónde entraste. Este es el orden
// que tenía el bundle único; el CSS construido quedó idéntico byte a byte.
import "./styles.css";
import "./styles/organizer-premium.css";
import "./styles/anual.css";
import "./components/forjito/forjito.css";
import "react-easy-crop/react-easy-crop.css";
import "./styles/roster.css";
import "./styles/tiebreak.css";
import "./styles/accounts.css";
import "./styles/fairplay.css";
import "./styles/player.css";
import "./components/RosterList.css";
import "./styles/descargar.css";
import "./styles/organizer-app.css";
import "./styles/schedule-lock.css";
import "./styles/organizer-jornada.css";
import { App } from "./App";
import { initSentry } from "./observability/sentry";
import { installClientErrorReporting } from "./api/errorReporting";

// Antes de renderizar: si el arranque mismo revienta, queremos que ESE error
// también llegue.
initSentry();
// Los errores que el usuario VE pero el backend nunca se entera (sin señal,
// servidor inalcanzable, la pantalla que se rompe) viajan al panel por la
// telemetría que ya existe. Sentry sigue siendo la herramienta del que
// programa; esto es para el dueño de la liga.
installClientErrorReporting();

// Un deploy nuevo borra los chunks del anterior. Quien tenía la web abierta y
// navega a una pantalla que todavía no había bajado pide un archivo que ya no
// existe: se recarga UNA vez y entra con la versión nueva. Si vuelve a fallar
// enseguida (sin red, no es un deploy), no se insiste y el error sigue su
// curso hasta el ErrorBoundary.
window.addEventListener("vite:preloadError", (evento) => {
  // Las herramientas pesadas (Excel, recorte de fondo) quedan FUERA del
  // precache a propósito y quien las llama ya muestra su propio error. Si acá
  // recargáramos, al organizador se le iría la pantalla con el archivo ya
  // elegido y el modal abierto — y encima volvería al mismo build.
  const detalle = String((evento as Event & { payload?: unknown }).payload ?? "");
  if (/xlsx|ort[.\-]|cutout\.worker/.test(detalle)) return;

  const CLAVE = "forja.recarga-por-version";
  try {
    const ultima = Number(sessionStorage.getItem(CLAVE) ?? 0);
    if (Date.now() - ultima < 30_000) return;
    sessionStorage.setItem(CLAVE, String(Date.now()));
  } catch {
    // Sin sessionStorage no hay cómo cortar un bucle: mejor no recargar.
    return;
  }
  evento.preventDefault();
  // Con la PWA instalada, recargar a secas sirve el MISMO build viejo: las
  // navegaciones las contesta el service worker desde su precache. Primero se
  // le pide que se actualice (queda listo de inmediato por skipWaiting) y
  // recién ahí se recarga; si tarda, se recarga igual a los 3 segundos.
  let recargado = false;
  const recargar = () => {
    if (recargado) return;
    recargado = true;
    window.location.reload();
  };
  window.setTimeout(recargar, 3000);
  const sw = navigator.serviceWorker;
  if (!sw) {
    recargar();
    return;
  }
  sw.getRegistration()
    .then((registro) => registro?.update())
    .catch(() => {})
    .finally(recargar);
});

const renderApp = () => ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {/* v7_startTransition: al navegar a una pantalla cuyo chunk todavía no
        bajó, se sigue viendo la anterior hasta que llega (en vez de la espera
        a pantalla completa tapando el shell). */}
    <BrowserRouter future={{ v7_startTransition: true }}>
      <ToastProvider>
        <ConfirmProvider>
          <ThemeProvider>
            <AuthProvider>
              <App />
            </AuthProvider>
          </ThemeProvider>
        </ConfirmProvider>
      </ToastProvider>
    </BrowserRouter>
  </React.StrictMode>
);

// El entorno aislado debe interceptar fetch ANTES de AuthProvider: una foto
// de sesión previa disparaba un 401 real mientras bajaba la pantalla diferida.
if (import.meta.env.DEV && location.pathname === "/vista-organizador") {
  void import("./pages/VistaOrganizadorPreview").then((preview) => {
    preview.installOrganizerPreview();
    renderApp();
  });
} else if (
  import.meta.env.DEV &&
  (location.pathname === "/vista-bienvenida" ||
    // La entrada «¿Qué venís a hacer?» con su Layout real, sin backend:
    // /onboarding?vista=1 (y ?vista=1&organizar=1 para el paso de la liga).
    (location.pathname === "/onboarding" && new URLSearchParams(location.search).has("vista")))
) {
  void import("./pages/VistaBienvenidaPreview").then((preview) => {
    preview.installBienvenidaPreview();
    renderApp();
  });
} else if (import.meta.env.DEV && location.pathname === "/vista-torneos") {
  void import("./pages/VistaTorneosPreview").then((preview) => {
    preview.installTorneosPreview();
    renderApp();
  });
} else renderApp();

// PWA: buscamos updates al cargar y cada 60 s. El SW usa autoUpdate +
// skipWaiting + clientsClaim, pero NO recargamos al tomar control: ese cambio
// puede ocurrir mientras alguien escribe el login o guarda un formulario. La
// app muestra un aviso y la recarga queda en manos del usuario.
if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
  let hasSeenController = Boolean(navigator.serviceWorker.controller);
  // Cualquier tecleo en un campo marca la página como "con trabajo en curso"
  // (lo lee también PwaUpdateNotice para decidir si puede recargar sola).
  window.addEventListener(
    "input",
    () => { document.documentElement.dataset.forjaEditado = "1"; },
    true,
  );
  // Elegir una imagen también es trabajo en curso, y hay que marcarlo AL
  // ABRIR el selector, no al volver: la galería manda la página a segundo
  // plano, y al volver el aviso de versión nueva la recargaba antes de que
  // llegara el archivo. Se perdía la foto y el recortador no se abría ("elijo
  // la imagen y me vuelve atrás"), sólo cuando había un deploy pendiente.
  window.addEventListener(
    "click",
    (e) => {
      const el = e.target;
      if (el instanceof HTMLInputElement && el.type === "file") {
        document.documentElement.dataset.forjaEditado = "1";
      }
    },
    true,
  );
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    // La primera instalación controla esta misma página, pero ya cargó el
    // build actual: no hay nada que actualizar. Los cambios siguientes sí son
    // un deploy nuevo y se anuncian sin interrumpir el trabajo en curso.
    if (!hasSeenController) {
      hasSeenController = true;
      return;
    }
    // Recién cargada y sin que la persona haya tocado ningún campo, no hay
    // nada que perder: se recarga sola en vez de preguntar. Un tope por
    // sesión evita un bucle si el SW cambiara de control repetidamente.
    const segundosDesdeCarga = performance.now() / 1000;
    let yaAuto = false;
    try {
      const ultimo = Number(sessionStorage.getItem("forja:pwa-auto-reload") ?? 0);
      yaAuto = Date.now() - ultimo < 60_000;
    } catch {
      yaAuto = true;
    }
    if (segundosDesdeCarga < 20 && !document.documentElement.dataset.forjaEditado && !yaAuto) {
      try {
        sessionStorage.setItem("forja:pwa-auto-reload", String(Date.now()));
      } catch {
        /* sin storage: no se auto-recarga */
        return;
      }
      window.location.reload();
      return;
    }
    document.documentElement.dataset.forjaPwaUpdate = "ready";
    window.dispatchEvent(new Event("forja:pwa-update-ready"));
  });
  navigator.serviceWorker.ready
    .then((registration) => {
      registration.update().catch(() => {});
      window.setInterval(() => registration.update().catch(() => {}), 60_000);
    })
    .catch(() => {});
}
